/**
 * Utility functions for Must Win deep linking
 * Allows sharing direct links to specific Must Win filter states
 */

import {
  ViewStateParams,
  generateViewStateDeepLink
} from './viewStateDeepLink';

export interface MustWinDeepLinkParams {
  type: 'audience' | 'productGroup' | 'category' | 'product' | 'multi';
  value: string;
  label: string;
  filters?: {
    audiences?: string[];
    categories?: string[];
    products?: string[];
    productGroups?: string[];
  };
  /** Current roadmap date range so deep links match Share View behavior */
  viewContext?: Pick<ViewStateParams, 'startDate' | 'endDate'>;
}

function buildViewStateForMustWinLink(
  params: MustWinDeepLinkParams
): ViewStateParams {
  const viewState: ViewStateParams = {
    startDate: params.viewContext?.startDate,
    endDate: params.viewContext?.endDate,
    mustWin: true,
    mustWinValue: params.value,
    mustWinLabel: params.label
  };

  if (params.type !== 'multi') {
    viewState.mustWinType = params.type;
  }

  if (params.filters) {
    if (params.filters.audiences?.length) {
      viewState.audiences = params.filters.audiences;
    }
    if (params.filters.categories?.length) {
      viewState.categories = params.filters.categories;
    }
    if (params.filters.products?.length) {
      viewState.products = params.filters.products;
    }
    if (params.filters.productGroups?.length) {
      viewState.productGroups = params.filters.productGroups;
    }
  } else {
    switch (params.type) {
      case 'audience':
        viewState.audiences = [params.value];
        break;
      case 'productGroup':
        viewState.productGroups = [params.value];
        break;
      case 'category':
        viewState.categories = [params.value];
        break;
      case 'product':
        viewState.products = [params.value];
        break;
      default:
        break;
    }
  }

  return viewState;
}

/**
 * Generate a deep link URL for a Must Win filter
 * @param params - The Must Win filter parameters
 * @returns Full URL with query parameters
 */
export function generateMustWinDeepLink(params: MustWinDeepLinkParams): string {
  let url = generateViewStateDeepLink(buildViewStateForMustWinLink(params));

  if (params.type === 'multi') {
    const urlObj = new URL(url);
    urlObj.searchParams.set('mustWinType', 'multi');
    if (params.filters) {
      urlObj.searchParams.set('mustWinFilters', JSON.stringify(params.filters));
    }
    url = urlObj.toString();
  }

  return url;
}

/**
 * Parse Must Win parameters from URL
 * @returns MustWinDeepLinkParams if present in URL, null otherwise
 */
export function parseMustWinDeepLink(): MustWinDeepLinkParams | null {
  const searchParams = new URLSearchParams(window.location.search);

  const mustWin = searchParams.get('mustWin');
  const type = searchParams.get('mustWinType') as
    | 'audience'
    | 'productGroup'
    | 'category'
    | 'product'
    | 'multi'
    | null;
  const value = searchParams.get('mustWinValue');
  const label = searchParams.get('mustWinLabel');
  const filtersParam = searchParams.get('mustWinFilters');
  let filters: MustWinDeepLinkParams['filters'] | undefined;

  if (filtersParam) {
    try {
      const parsed = JSON.parse(filtersParam);
      if (parsed && typeof parsed === 'object') {
        filters = parsed;
      }
    } catch (e) {
      // Ignore invalid filters
    }
  }

  // Check if all required parameters are present
  if (mustWin === 'yes' && type && value && label) {
    return {
      type,
      value,
      label,
      filters
    };
  }

  return null;
}

/**
 * Copy Must Win deep link to clipboard
 * @param params - The Must Win filter parameters
 * @returns Promise that resolves when copied successfully
 */
export async function copyMustWinDeepLink(
  params: MustWinDeepLinkParams
): Promise<void> {
  const link = generateMustWinDeepLink(params);
  await navigator.clipboard.writeText(link);
}

/**
 * Remove Must Win parameters from URL without page reload
 */
export function clearMustWinDeepLinkParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('mustWin');
  url.searchParams.delete('mustWinType');
  url.searchParams.delete('mustWinValue');
  url.searchParams.delete('mustWinLabel');
  url.searchParams.delete('mustWinFilters');

  window.history.replaceState({}, '', url.toString());
}
